import React, { useState } from 'react';
import { Bot, Sparkles, Zap, CheckCircle2, Sliders, Play, Plus, RefreshCw, ShieldAlert, Cpu } from 'lucide-react';

export default function AiStudio({ workflows, onToggleWorkflow, onAddWorkflow, projects, team }) {
  const [promptText, setPromptText] = useState('');
  const [aiOutput, setAiOutput] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [showNewWfModal, setShowNewWfModal] = useState(false);
  const [wfName, setWfName] = useState('');
  const [wfTrigger, setWfTrigger] = useState('Task status changes to "Review"');
  const [wfAction, setWfAction] = useState('Assign to Lead QA & notify Slack');

  const handleRunAi = (e) => {
    e.preventDefault();
    if (!promptText.trim()) return;
    setIsGenerating(true);
    setAiOutput('');

    setTimeout(() => {
      setIsGenerating(false);
      setAiOutput(`### 🤖 Nexus AI Analysis & Execution Report
Query: "${promptText}"

1. **Sprint Velocity Risk**: Low risk detected. Team Cloud Infrastructure is on track for May 15 deadline.
2. **Resource Optimization**: Assignee Alex Rivera has 92% workload. Recommended re-balancing 2 tasks to Marcus Chen.
3. **Automated Recommendation**: Generated 3 sub-tasks for Kubernetes ingress controllers and security certificates.`);
    }, 1200);
  };

  const handleCreateWf = (e) => {
    e.preventDefault();
    if (!wfName.trim()) return;
    const newWf = {
      id: 'wf-' + Date.now(),
      name: wfName.trim(),
      trigger: wfTrigger,
      action: wfAction,
      active: true,
      executions: 0
    };
    onAddWorkflow(newWf);
    setShowNewWfModal(false);
    setWfName('');
  };

  return (
    <div className="p-8 space-y-8 animate-fadeIn">
      
      {/* Header */}
      <div className="bg-gradient-to-r from-violet-900 via-indigo-900 to-slate-900 rounded-3xl p-8 text-white shadow-xl relative overflow-hidden flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
        <div className="absolute right-0 top-0 w-80 h-80 bg-violet-500/20 rounded-full blur-3xl pointer-events-none"></div>
        <div className="space-y-2 relative z-10">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-violet-700/60 rounded-full text-xs font-medium text-violet-200">
            <Bot className="w-3.5 h-3.5 text-indigo-300" /> Neural Workflow & Copilot Studio
          </div>
          <h1 className="text-2xl md:text-3xl font-extrabold tracking-tight">AI Studio & Automations</h1>
          <p className="text-violet-200 text-sm max-w-xl">
            Empower your engineering teams with autonomous AI agents, intelligent task triage, and custom workflow automation rules.
          </p>
        </div>
        <div className="relative z-10">
          <button
            onClick={() => setShowNewWfModal(true)}
            className="px-5 py-3 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl font-semibold text-sm transition shadow-lg flex items-center gap-2"
          >
            <Plus className="w-4 h-4" /> Create Automation Rule
          </button>
        </div>
      </div>

      {/* AI Prompt Copilot Section */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6 space-y-6">
        <div className="flex items-center space-x-3">
          <div className="p-2.5 bg-indigo-100 text-indigo-700 rounded-xl">
            <Sparkles className="w-6 h-6" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-slate-900">Nexus AI Copilot Assistant</h2>
            <p className="text-xs text-slate-500">Ask anything about project status, sprint burndown, or code architecture</p>
          </div>
        </div>

        <form onSubmit={handleRunAi} className="space-y-4">
          <div className="relative">
            <textarea
              rows={3}
              placeholder="e.g. Generate a comprehensive risk report for Cloud Infrastructure Migration and suggest mitigation strategies..."
              value={promptText}
              onChange={(e) => setPromptText(e.target.value)}
              className="w-full bg-slate-50 border border-slate-200 rounded-2xl p-4 text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:bg-white transition"
            />
          </div>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2 text-xs text-slate-500">
              <Cpu className="w-4 h-4 text-indigo-600" />
              <span>Powered by NexusLLM-v4 (GPT-4o Enterprise Fine-tuned)</span>
            </div>
            <button
              type="submit"
              disabled={isGenerating}
              className="px-6 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold text-sm rounded-xl transition shadow-md shadow-indigo-600/20 flex items-center gap-2 disabled:opacity-50"
            >
              {isGenerating ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" /> Analyzing...
                </>
              ) : (
                <>
                  <Play className="w-4 h-4" /> Run AI Analysis
                </>
              )}
            </button>
          </div>
        </form>

        {aiOutput && (
          <div className="p-6 bg-slate-900 text-slate-100 rounded-2xl border border-slate-800 space-y-3 font-mono text-xs leading-relaxed animate-fadeIn">
            <div className="flex items-center justify-between border-b border-slate-800 pb-3 text-indigo-400 font-bold">
              <span className="flex items-center gap-2"><Bot className="w-4 h-4" /> Nexus AI Output</span>
              <span>Execution Time: 0.84s</span>
            </div>
            <div className="whitespace-pre-line text-slate-300">{aiOutput}</div>
          </div>
        )}
      </div>

      {/* Active Automation Rules */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold text-slate-900">Workflow Automation Rules</h2>
            <p className="text-xs text-slate-500">Trigger-action event pipelines running in real-time</p>
          </div>
          <span className="text-xs font-semibold px-2.5 py-1 bg-indigo-50 text-indigo-700 rounded-full">
            {workflows.filter(w => w.active).length} Active Rules
          </span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {workflows.map(wf => (
            <div key={wf.id} className="bg-slate-50 p-5 rounded-2xl border border-slate-200/80 space-y-4 flex flex-col justify-between">
              <div className="space-y-3">
                <div className="flex items-start justify-between">
                  <div className="p-2 bg-indigo-100 text-indigo-700 rounded-xl">
                    <Zap className="w-4 h-4" />
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input 
                      type="checkbox" 
                      checked={wf.active}
                      onChange={() => onToggleWorkflow(wf.id)}
                      className="sr-only peer"
                    />
                    <div className="w-9 h-5 bg-slate-300 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-indigo-600"></div>
                  </label>
                </div>

                <h3 className="text-base font-bold text-slate-900">{wf.name}</h3>
                
                <div className="space-y-2 text-xs">
                  <div className="p-2.5 bg-white rounded-xl border border-slate-200/60">
                    <span className="font-bold text-slate-500 block text-[10px] uppercase mb-0.5">Trigger</span>
                    <span className="text-slate-800 font-medium">{wf.trigger}</span>
                  </div>
                  <div className="p-2.5 bg-white rounded-xl border border-slate-200/60">
                    <span className="font-bold text-slate-500 block text-[10px] uppercase mb-0.5">Action</span>
                    <span className="text-indigo-700 font-semibold">{wf.action}</span>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between pt-3 border-t border-slate-200 text-xs text-slate-500">
                <span>Executions: <strong className="text-slate-900">{wf.executions}</strong></span>
                <span className={`px-2 py-0.5 rounded-full font-semibold ${wf.active ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-200 text-slate-600'}`}>
                  {wf.active ? 'Active' : 'Paused'}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Create Workflow Modal */}
      {showNewWfModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4 animate-fadeIn">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg p-6 space-y-6">
            <h2 className="text-lg font-bold text-slate-900">Create Workflow Automation</h2>
            
            <form onSubmit={handleCreateWf} className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-slate-600 uppercase mb-1">Workflow Name</label>
                <input 
                  type="text"
                  required
                  placeholder="e.g. Notify Slack on High Priority Bug"
                  value={wfName}
                  onChange={(e) => setWfName(e.target.value)}
                  className="w-full text-sm bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2.5 focus:outline-none focus:ring-2 focus:ring-indigo-500 font-medium"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-600 uppercase mb-1">Trigger Event</label>
                <input 
                  type="text"
                  required
                  value={wfTrigger}
                  onChange={(e) => setWfTrigger(e.target.value)}
                  className="w-full text-sm bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2.5 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-600 uppercase mb-1">Automated Action</label>
                <input 
                  type="text"
                  required
                  value={wfAction}
                  onChange={(e) => setWfAction(e.target.value)}
                  className="w-full text-sm bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2.5 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              <div className="pt-4 border-t border-slate-100 flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setShowNewWfModal(false)}
                  className="px-5 py-2.5 border border-slate-200 text-slate-700 text-sm font-medium rounded-xl hover:bg-slate-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-5 py-2.5 bg-indigo-600 text-white text-sm font-semibold rounded-xl hover:bg-indigo-700 shadow-md shadow-indigo-600/20"
                >
                  Save & Activate
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
}
